


import { Component, Emit, Prop, Vue } from "vue-property-decorator";

import { Configuration, Subscriptions, SubscriptionAudit } from "App";
import {
    Field, DiscountDetails, Subscription, Invoice, Coupon, GlobalConfiguration, CustomError,
    SubscriptionAudit as SubscriptionAuditEntry
} from "App/Models";

import { EventBus } from "App/IOC";
import { Dictionary } from "App/Contracts";

import Tab from "Web/Components/Tabs/Tab.vue";
import Tabs from "Web/Components/Tabs/Index.vue";

import Form from "Web/Components/Form/Index.vue";
import FormField from "Web/Components/Form/Field.vue";
import FormFieldset from "Web/Components/Form/Fieldset.vue";

/* One line of a billing snapshot stored on a snapshot-style audit row. Same shape as the migration
   history snapshot (label, quantity, unit price, subtotal, included flag, price ids). */
interface HistorySnapshotLine {
    Label: string;
    Quantity?: number;
    Unit?: number;
    Amount: number;
    Included?: boolean;
    PriceId?: number;
    PriceVersionId?: number;
}

/* Parsed before / after billing snapshot of a subscription change. */
interface HistorySnapshot {
    Recurrence?: string;
    Coupons?: string;
    Total: number;
    Lines: HistorySnapshotLine[];
}

/* One row of the before / after detail table: the same line on both sides, paired by PriceId. */
interface HistoryDetailRow {
    Label: string;
    Before?: HistorySnapshotLine;
    After?: HistorySnapshotLine;
    Changed: boolean;
}

@Component({
    components: {
        Field: FormField,
        Fieldset: FormFieldset,
        Form,
        Tab,
        Tabs
    }
})
export default class SubscriptionDetails extends Vue {
    @Prop()
    Record?: Subscription;

    fields: Dictionary<Field> = {};
    configurationFields: Dictionary<Field> = {};
    Invoices: Invoice[] = [];
    appliedCoupon?: Coupon = {} as Coupon;
    configuration: GlobalConfiguration | undefined = {} as GlobalConfiguration;
    loading: boolean = true;

    /* Historique des changements de l'abonnement (onglet dédié, chargé au montage). */
    history: SubscriptionAuditEntry[] = [];
    historyLoading: boolean = false;
    historyLoaded: boolean = false;

    /* Id de l'entrée d'historique dont le détail avant / après est déployé (-1 = aucun). Init à un
       nombre réel et non undefined : un champ initialisé à undefined n'est pas rendu réactif par le
       transpile ici, donc le toggle changerait la valeur sans re-render. */
    historyExpandedId: number = -1;

    /* Ids des entrées dont le résumé (liste des lignes changées) est déplié en entier. Array et non
       Set pour rester réactif sous le transpile node12. */
    summaryExpandedIds: number[] = [];

    created() {
        this.appliedCoupon = undefined;
    }

    async mounted(): Promise<void> {
        this.fields = await Subscriptions.Fields.Get();
        this.configurationFields = await Configuration.Fields.Get();

        if( this.Record !== undefined ) {
            this.Invoices = this.Record.Invoices !== undefined ? this.Record.Invoices : [];

            if( this.Record.Coupons !== undefined )
                this.appliedCoupon = this.Record.Coupons[ 0 ];
        }

        const [ configuration, error ] = await Configuration.Get();

        if( error !== undefined || configuration === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.configuration = configuration;

        this.loading = false;

        await this.loadHistory();
    }

    private redirectToCoupon() {
        if( this.appliedCoupon === undefined )
            return;

        this.$router.push( `/coupons/${this.appliedCoupon.Id}` )
    }

    private get discount(): DiscountDetails | undefined {
        if( this.Record === undefined )
            return undefined;

        return this.Record.DiscountDetails;
    }

    private get discountExpired(): boolean | undefined {
        if( this.discount === undefined || this.discount.Expired === undefined )
            return undefined;

        return this.discount.Expired;
    }

    private get discountMessage(): string | undefined {
        if( this.Record === undefined )
            return undefined;

        if( this.discountExpired )
            return ( this.Record.Recurrence === "Yearly" )
                ? "SUBSCRIPTIONS_yearlyDiscountExpired"
                : "SUBSCRIPTIONS_discountExpired";

        else
            return ( this.Record.Recurrence === "Yearly" )
                ? "SUBSCRIPTIONS_yearlyDiscountActivated"
                : "SUBSCRIPTIONS_discountActivated";
    }

    private async DownloadInvoice( invoice: Invoice ) {
        if( invoice.DownloadURL === undefined ) {
            EventBus.Publish( "Error", CustomError.User( $t( "SUBSCRIPTION_invoiceUndefined" ) ) );
            return;
        }

        window.location.href = invoice.DownloadURL;
    }

    private getInvoiceStatus( status: string ): string {
        switch( true ) {
            case status === "Paid":
                return $t( "SUBSCRIPTION_paid" );

            case status === "Open":
                return $t( "SUBSCRIPTION_open" );

            case status === "Uncollectible":
                return $t( "SUBSCRIPTION_uncollectible" );

            default:
                return $t( "SUBSCRIPTION_void" );
        }
    }

    /* Extrait le montant numérique d'une ligne quel que soit le type renvoyé. */
    private lineAmount( line: any ): number {
        const subtotal = line.Subtotal;

        if( subtotal === undefined || subtotal === null )
            return 0;

        if( typeof subtotal === "number" )
            return subtotal;

        if( typeof subtotal.ToFixed === "function" )
            return Number( subtotal.ToFixed( 2 ) );

        return Number( subtotal ) || 0;
    }

    private formatAmount( value: number ): string {
        return value.toLocaleString( "fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    /* Regroupe les lignes par cible (Target) : titre = cible, features = produits du
       groupe, description = description du produit, prix = somme des sous-totaux. */
    /* Décimal éventuel (objet ToFixed) ou primitif → nombre. */
    private toNumber( value: any ): number {
        if( value === undefined || value === null )
            return 0;

        if( typeof value === "number" )
            return value;

        if( typeof value.ToFixed === "function" )
            return Number( value.ToFixed( 2 ) );

        return Number( value ) || 0;
    }

    /* Pour une ligne à 0 €, distingue la raison de la gratuité :
       - "included" : produit inclus par défaut dans l'offre.
       - "tier"     : premier palier de volume gratuit (UnitPrice du 1er tier = 0). */
    private freeReason( line: any ): { kind: "included" | "tier"; limit?: number } | undefined {
        const price = line.Price;

        if( price === undefined )
            return undefined;

        if( price.Included === true )
            return { kind: "included" };

        const tiered = ( price.Type === "Volume" ) || ( price.Type === "Graduated" );
        const firstTier = Array.isArray( price.Tiers ) ? price.Tiers[ 0 ] : undefined;

        if( tiered && firstTier !== undefined && this.toNumber( firstTier.UnitPrice ) === 0 )
            return { kind: "tier", limit: this.toNumber( firstTier.MaxQuantity ) };

        return undefined;
    }

    private get priceGroups(): {
        key: string;
        title: string;
        description: string;
        features: string[];
        total: number;
        freeKind?: "included" | "tier";
        freeLimit?: number;
    }[] {
        if( this.Record === undefined || this.Record.Lines === undefined )
            return [];

        const groups: {
            key: string;
            title: string;
            description: string;
            features: string[];
            total: number;
            freeKind?: "included" | "tier";
            freeLimit?: number;
        }[] = [];

        for( const line of this.Record.Lines ) {
            /* Skip one-shot global prices (Offres / frais de mise en service: Price.Recurrence
               undefined). They are billed once at signature and never on a renewal, so on this
               tab — which lists the prices applied to the next billing cycle — an already-paid
               one-shot must not appear. Recurring lines, included features and free tiers all
               carry a Recurrence and are kept. */
            if( line.Price !== undefined && line.Price.Recurrence === undefined )
                continue;

            const target = ( line.Price !== undefined ) ? line.Price.Target : undefined;

            const title = ( target !== undefined && target.DisplayName )
                ? target.DisplayName
                : ( ( target !== undefined && target.Name )
                    ? target.Name
                    : ( line.Product.Name || "" ) );

            let group = groups.find( current => current.key === title );

            if( group === undefined ) {
                group = { key: title, title, description: "", features: [], total: 0 };
                groups.push( group );
            }

            const productName = line.Product.Name || "";

            if( productName !== "" && group.features.indexOf( productName ) === -1 )
                group.features.push( productName );

            if( group.description === "" && line.Product.Description )
                group.description = line.Product.Description;

            group.total += this.lineAmount( line );

            /* Mémorise la raison de gratuité au niveau groupe (1er motif rencontré). */
            if( group.freeKind === undefined ) {
                const reason = this.freeReason( line );

                if( reason !== undefined ) {
                    group.freeKind = reason.kind;
                    group.freeLimit = reason.limit;
                }
            }
        }

        /* Produits payants en premier, produits à 0 € ensuite (ordre stable). */
        const paid = groups.filter( group => group.total > 0 );
        const free = groups.filter( group => group.total <= 0 );

        return [ ...paid, ...free ];
    }

    /* Charge l'historique des changements (onglet dédié). */
    private async loadHistory(): Promise<void> {
        if( this.historyLoaded || this.Record === undefined )
            return;

        this.historyLoading = true;

        const [ entries, error ] = await SubscriptionAudit.FindBySubscription( this.Record.Id );

        this.historyLoading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.history = entries;
        this.historyLoaded = true;
    }

    /* Libellé lisible du champ audité (Recurrence, Quantity:<plan>, Coupon, Total). */
    private auditFieldLabel( field?: string ): string {
        if( field === undefined )
            return "";

        if( field.indexOf( "Quantity:" ) === 0 ) {
            const plan = field.slice( "Quantity:".length );

            return `${ $t( "SUBSCRIPTION_HISTORY_quantity" ) } (${ plan })`;
        }

        /* Per-line price move logged as "Amount:<line label>" -> show "Prix (Utilisateurs)". */
        if( field.indexOf( "Amount:" ) === 0 ) {
            const line = field.slice( "Amount:".length );

            return `${ $t( "SUBSCRIPTION_HISTORY_amount" ) } (${ line })`;
        }

        switch( field ) {
            case "Recurrence":
                return $t( "SUBSCRIPTION_recurrenceLabel" );

            case "Coupon":
                return $t( "SUBSCRIPTIONS_fieldCoupon" );

            case "Total":
                return $t( "GLOBAL_total" );

            default:
                return field;
        }
    }

    /* Valeur affichable : traduit la récurrence, formate les quantités illimitées en "Illimité",
       et formate le total (séparateur de milliers + €). */
    private auditValue( field: string | undefined, value?: string ): string {
        if( value === undefined || value === "" )
            return "—";

        if( field === "Recurrence" )
            return value === "Yearly"
                ? $t( "SUBSCRIPTIONS_recurrenceYearly" )
                : $t( "SUBSCRIPTIONS_recurrenceMonthly" );

        if( field === "Total" || ( field !== undefined && field.indexOf( "Amount:" ) === 0 ) ) {
            const amount = Number( value );

            return isNaN( amount )
                ? value
                : `${ this.formatAmount( amount ) } ${ $t( "GLOBAL_currency" ) } `
                    + `${ $t( "GLOBAL_excludingTaxSuffix" ) }`;
        }

        /* Quantités : la valeur sentinelle de quantité illimitée s'affiche "Illimité". */
        if( field !== undefined && field.indexOf( "Quantity:" ) === 0 ) {
            const quantity = Number( value );
            const unlimited = ( this.configuration !== undefined )
                ? this.toNumber( this.configuration.UnlimitedQuantity )
                : 0;

            if( !isNaN( quantity ) && unlimited > 0 && quantity === unlimited )
                return $t( "SUBSCRIPTIONS_unlimitedQuantity" );
        }

        return value;
    }

    /* A snapshot-style row carries before / after billing snapshots (rendered as an expandable
       before / after table); a legacy row only has Field / OldValue / NewValue. */
    private isSnapshotEntry( entry: SubscriptionAuditEntry ): boolean {
        return entry.Before !== undefined && entry.Before !== ""
            && entry.After !== undefined && entry.After !== "";
    }

    private toggleHistoryDetail( entry: SubscriptionAuditEntry ): void {
        this.historyExpandedId = this.historyExpandedId === entry.Id ? -1 : entry.Id;
    }

    private historyDetailRowClass( row: HistoryDetailRow ): string {
        return row.Changed ? "c-sub-history-detail-changed" : "";
    }

    private parseHistorySnapshot( raw?: string ): HistorySnapshot | undefined {
        if( raw === undefined || raw === "" )
            return undefined;

        try {
            return JSON.parse( raw ) as HistorySnapshot;

        } catch( exception ) {
            return undefined;
        }
    }

    /* Merge before / after snapshots into one row per line, paired by PriceId (stable across the
       archive / recreate a change performs). Paying lines float above the 0 € / included lines. */
    private historyDetailRows( entry: SubscriptionAuditEntry ): HistoryDetailRow[] {
        const before = this.parseHistorySnapshot( entry.Before );
        const after = this.parseHistorySnapshot( entry.After );

        const beforeLines = before !== undefined ? before.Lines : [];
        const afterLines = after !== undefined ? after.Lines : [];

        const keyOf = ( line: HistorySnapshotLine ): number =>
            line.PriceId !== undefined ? line.PriceId : -1;

        const beforeByKey = new Map<number, HistorySnapshotLine>();
        for( const line of beforeLines )
            beforeByKey.set( keyOf( line ), line );

        const afterByKey = new Map<number, HistorySnapshotLine>();
        for( const line of afterLines )
            afterByKey.set( keyOf( line ), line );

        /* Order: after lines first (current state), then before-only lines (removed). */
        const orderedKeys: number[] = [];
        for( const line of afterLines )
            orderedKeys.push( keyOf( line ) );
        for( const line of beforeLines )
            if( !afterByKey.has( keyOf( line ) ) )
                orderedKeys.push( keyOf( line ) );

        const rows: HistoryDetailRow[] = orderedKeys.map(
            ( key: number ): HistoryDetailRow => {
                const beforeLine = beforeByKey.get( key );
                const afterLine = afterByKey.get( key );

                const beforeAmount = beforeLine !== undefined ? beforeLine.Amount : 0;
                const afterAmount = afterLine !== undefined ? afterLine.Amount : 0;
                const beforeUnit = beforeLine !== undefined ? beforeLine.Unit : undefined;
                const afterUnit = afterLine !== undefined ? afterLine.Unit : undefined;

                return {
                    Label: afterLine !== undefined
                        ? afterLine.Label
                        : ( beforeLine !== undefined ? beforeLine.Label : "—" ),
                    Before: beforeLine,
                    After: afterLine,
                    Changed: beforeAmount !== afterAmount || beforeUnit !== afterUnit
                };
            }
        );

        const isPaying = ( row: HistoryDetailRow ): boolean => {
            const beforeAmount = row.Before !== undefined ? row.Before.Amount : 0;
            const afterAmount = row.After !== undefined ? row.After.Amount : 0;

            return beforeAmount !== 0 || afterAmount !== 0;
        };

        return rows.sort(
            ( a: HistoryDetailRow, b: HistoryDetailRow ): number =>
                Number( isPaying( b ) ) - Number( isPaying( a ) )
        );
    }

    /* "3 × 46,00 € = 138,00 €" when quantity / unit are known, else the subtotal, else "—".
       Included features mirror the "Produits et prix" view ("Inclus jusqu'à N"). */
    private formatSnapshotLine( line?: HistorySnapshotLine ): string {
        if( line === undefined )
            return "—";

        if( line.Included === true )
            return $t( "SUBSCRIPTIONMIGRATION_included", { quantity: line.Quantity } );

        if( line.Quantity === 1 || line.Quantity === undefined || line.Unit === undefined )
            return `${ this.formatAmount( line.Amount ) } € ${ $t( "GLOBAL_excludingTaxSuffix" ) }`;

        return `${ line.Quantity } × ${ this.formatAmount( line.Unit ) } €`
            + ` = ${ this.formatAmount( line.Amount ) } € ${ $t( "GLOBAL_excludingTaxSuffix" ) }`;
    }

    private historySnapshotTotal( raw?: string ): string {
        const snapshot = this.parseHistorySnapshot( raw );

        return snapshot !== undefined
            ? `${ this.formatAmount( snapshot.Total ) } € ${ $t( "GLOBAL_excludingTaxSuffix" ) }`
            : "—";
    }

    private snapshotRecurrenceLabel( raw?: string ): string {
        const snapshot = this.parseHistorySnapshot( raw );

        if( snapshot === undefined || snapshot.Recurrence === undefined )
            return "";

        return snapshot.Recurrence === "Yearly"
            ? $t( "SUBSCRIPTIONS_recurrenceYearly" )
            : $t( "SUBSCRIPTIONS_recurrenceMonthly" );
    }

    /* Libellé coupon figé dans le snapshot (nom + valeur + durée + date d'application). On le lit
       depuis l'historique car l'abonnement perd l'info une fois le coupon consommé. */
    private snapshotCouponLabel( raw?: string ): string {
        const snapshot = this.parseHistorySnapshot( raw );

        if( snapshot === undefined || snapshot.Coupons === undefined )
            return "";

        return snapshot.Coupons;
    }

    /* Collapsed-row summary: what changed (récurrence, coupon, line labels), so the row reads
       without expanding. Falls back to the total move. */
    private changeSummary( entry: SubscriptionAuditEntry ): string {
        const before = this.parseHistorySnapshot( entry.Before );
        const after = this.parseHistorySnapshot( entry.After );

        if( before === undefined || after === undefined )
            return "—";

        const parts: string[] = [];

        if( ( before.Recurrence || "" ) !== ( after.Recurrence || "" ) )
            parts.push( $t( "SUBSCRIPTION_recurrenceLabel" ) );

        if( ( before.Coupons || "" ) !== ( after.Coupons || "" ) )
            parts.push( $t( "SUBSCRIPTIONS_fieldCoupon" ) );

        const changedLabels = this.historyDetailRows( entry )
            .filter( ( row: HistoryDetailRow ): boolean => row.Changed )
            .map( ( row: HistoryDetailRow ): string => row.Label );

        parts.push( ...changedLabels );

        return parts.length > 0 ? parts.join( ", " ) : $t( "GLOBAL_total" );
    }

    /* Résumé long → clamp 2 lignes + voir plus / voir moins. Seuil sur le nombre de caractères :
       au-delà la cellule déborde sur plusieurs lignes. */
    private isSummaryLong( entry: SubscriptionAuditEntry ): boolean {
        return this.changeSummary( entry ).length > 60;
    }

    private isSummaryExpanded( entry: SubscriptionAuditEntry ): boolean {
        return this.summaryExpandedIds.indexOf( entry.Id ) !== -1;
    }

    private toggleSummary( entry: SubscriptionAuditEntry ): void {
        const index = this.summaryExpandedIds.indexOf( entry.Id );

        if( index === -1 )
            this.summaryExpandedIds.push( entry.Id );
        else
            this.summaryExpandedIds.splice( index, 1 );
    }

    @Emit( "Refresh" )
    private refresh() {}
}
